import { useState } from 'react';

import { brand } from '@brand';
import { Button, Card } from '@/design-system/primitives';
import {
  usePaymentMethods,
  useSetDefaultPaymentMethod,
  useDeletePaymentMethod,
} from '@/lib/api/hooks';
import { cardLabel, cardExpiry } from '@/lib/payment-cards';
import { AddCardForm } from '@/features/marketplace/components/AddCardForm';

/**
 * Patient-facing saved-card manager. Lives in Account → "Payment methods".
 * Lists saved cards, marks the default (the card off-session subscription
 * charges use), and lets the patient set a different default, remove a card,
 * or add a new one (typed card or Apple Pay / Google Pay via AddCardForm).
 */
export function PaymentMethodsSection() {
  const { data, isLoading, isError } = usePaymentMethods();
  const setDefault = useSetDefaultPaymentMethod();
  const del = useDeletePaymentMethod();
  const [adding, setAdding] = useState(false);

  const cards = data?.payment_methods ?? [];
  const defaultId = data?.default_payment_method_id ?? null;
  // A subscription needs a card on file to renew, so the last card can't be
  // removed — patients replacing a card add the new one first (it becomes the
  // default), then remove the old one.
  const onlyCard = cards.length <= 1;

  return (
    <Card padding="lg">
      <Card.Header>
        <Card.Title>Payment methods</Card.Title>
      </Card.Header>
      <Card.Body>
        {isLoading ? (
          <p className="type-body-sm text-ink-muted">Loading…</p>
        ) : isError ? (
          <p className="type-body-sm text-status-danger">
            Couldn’t load your payment methods.
          </p>
        ) : cards.length === 0 && !adding ? (
          <div className="flex flex-col gap-12">
            <p className="type-body-sm text-ink-muted">No cards saved yet.</p>
            <Button size="sm" onClick={() => setAdding(true)}>
              Add a card
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-12">
            <ul className="flex flex-col gap-10">
              {cards.map((pm) => {
                const isDefault = pm.id === defaultId;
                return (
                  <li
                    key={pm.id}
                    className="flex items-center justify-between gap-15 border border-line-subtle px-15 py-12 flex-wrap"
                  >
                    <div className="min-w-0">
                      <div className="type-body-md text-ink truncate">
                        {cardLabel(pm)}
                        {isDefault && (
                          <span className="ml-10 type-label-sm text-ink-muted uppercase tracking-tight">
                            Default
                          </span>
                        )}
                      </div>
                      {cardExpiry(pm) && (
                        <div className="type-label-sm text-ink-muted mt-2">
                          {cardExpiry(pm)}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-8">
                      {!isDefault && (
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={
                            setDefault.isPending &&
                            setDefault.variables === pm.id
                          }
                          onClick={() => setDefault.mutate(pm.id)}
                        >
                          Make default
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={onlyCard}
                        title={
                          onlyCard
                            ? 'Add another card before removing this one — your subscription needs a card to renew.'
                            : undefined
                        }
                        loading={del.isPending && del.variables === pm.id}
                        onClick={() => del.mutate(pm.id)}
                      >
                        Remove
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* Storefront: the reason Remove is off, as text (a title
                tooltip never shows on phones). */}
            {brand.storefrontAccount && onlyCard && cards.length === 1 && (
              <p className="type-body-sm text-ink-muted leading-130">
                To remove this card, add another one first. Your plan needs a card on file to
                renew.
              </p>
            )}

            {del.isError && (
              <p className="type-body-sm text-status-danger">
                {(del.error as Error)?.message || 'Couldn’t remove that card.'}
              </p>
            )}

            {adding ? (
              <AddCardForm
                onAdded={(pmId) => {
                  setAdding(false);
                  // Newest card becomes the default (and is pushed onto the
                  // patient's subscriptions) so the next renewal charges it.
                  if (pmId) setDefault.mutate(pmId);
                }}
                onCancel={() => setAdding(false)}
              />
            ) : (
              <Button
                size="sm"
                variant="secondary"
                className="self-start"
                onClick={() => setAdding(true)}
              >
                Add card
              </Button>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
